import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowLeft, Mail, Key } from 'lucide-react';
import { getAdminUser } from '../utils/auth';
import toast from 'react-hot-toast';

const SuperAdminAuthPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState('email'); // 'email', 'code', 'reset'
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const SUPER_ADMIN_EMAIL = 'superadmin@thehautecircle.com';
  const TEMP_EMAIL = 'ed@shaktigroupindia.in'; // Temporary email for notifications

  const handleSuperAdminLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Check if this is the super admin
    if (formData.email !== SUPER_ADMIN_EMAIL) {
      setError('Access denied. Only Super Admin can access this area.');
      setLoading(false);
      return;
    }

    try {
      // Verify super admin credentials
      // For now, we'll use simple validation since we know the credentials
      if (formData.email === SUPER_ADMIN_EMAIL && formData.password === 'Haute@123') {
        console.log('✅ Super Admin authenticated');
        toast.success('Super Admin access granted!');
        navigate('/super-admin-den/management');
      } else {
        setError('Invalid super admin credentials.');
      }
    } catch (err) {
      console.error('❌ Super admin auth error:', err);
      setError('Authentication failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (step) => {
    setError('');
    setLoading(true);

    try {
      switch (step) {
        case 'sendCode':
          // Mock sending verification code
          if (forgotPasswordEmail !== SUPER_ADMIN_EMAIL) {
            setError('Only Super Admin can reset password.');
            setLoading(false);
            return;
          }
          
          // Simulate sending email
          console.log(`📧 Sending verification code to ${TEMP_EMAIL}`);
          toast.success(`Verification code sent to ${TEMP_EMAIL}`);
          setForgotPasswordStep('code');
          break;

        case 'verifyCode':
          // Mock code verification (in real app, this would be a backend call)
          if (verificationCode === '123456') {
            toast.success('Code verified successfully!');
            setForgotPasswordStep('reset');
          } else {
            setError('Invalid verification code. Try: 123456');
          }
          break;

        case 'resetPassword':
          if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            setLoading(false);
            return;
          }
          if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long.');
            setLoading(false);
            return;
          }
          
          // Mock password reset
          console.log('🔒 Password reset successful');
          toast.success('Password reset successful! Please login with your new password.');
          setShowForgotPassword(false);
          setForgotPasswordStep('email');
          setVerificationCode('');
          setNewPassword('');
          setConfirmPassword('');
          break;
      }
    } catch (err) {
      console.error('❌ Forgot password error:', err);
      setError('Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-gray-100">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="space-y-3 text-center">
            <div className="mx-auto w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-2">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Reset Super Admin Password</CardTitle>
            <CardDescription className="text-gray-600">
              {forgotPasswordStep === 'email' && 'Enter your email to receive verification code'}
              {forgotPasswordStep === 'code' && 'Enter the verification code sent to your email'}
              {forgotPasswordStep === 'reset' && 'Create your new password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
              {forgotPasswordStep === 'email' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="forgotEmail" className="text-gray-700 font-medium">Super Admin Email</Label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      placeholder="superadmin@thehautecircle.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      className="h-11"
                    />
                    <p className="text-xs text-gray-500">
                      Code will be sent to: {TEMP_EMAIL}
                    </p>
                  </div>
                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                      {error}
                    </div>
                  )}
                  <Button 
                    onClick={() => handleForgotPassword('sendCode')}
                    className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-medium" 
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </Button>
                </>
              )}

              {forgotPasswordStep === 'code' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="verificationCode" className="text-gray-700 font-medium">Verification Code</Label>
                    <Input
                      id="verificationCode"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      required
                      className="h-11 text-center text-lg tracking-widest"
                      maxLength={6}
                    />
                    <p className="text-xs text-gray-500">
                      For demo: use code <span className="font-mono font-bold">123456</span>
                    </p>
                  </div>
                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                      {error}
                    </div>
                  )}
                  <Button 
                    onClick={() => handleForgotPassword('verifyCode')}
                    className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-medium" 
                    disabled={loading}
                  >
                    {loading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                </>
              )}

              {forgotPasswordStep === 'reset' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-gray-700 font-medium">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                      {error}
                    </div>
                  )}
                  <Button 
                    onClick={() => handleForgotPassword('resetPassword')}
                    className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-medium" 
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </>
              )}

              <Button 
                variant="outline"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordStep('email');
                  setError('');
                }}
                className="w-full h-11"
              >
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-gray-100">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-2">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">Super Admin Den</CardTitle>
          <CardDescription className="text-gray-600">
            Restricted Access - Super Admin Authentication Required
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSuperAdminLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">Super Admin Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="superadmin@thehautecircle.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter super admin password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-11"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-medium transition-colors" 
              disabled={loading}
            >
              {loading ? 'Authenticating...' : 'Access Super Admin Den'}
            </Button>

            <Button 
              type="button"
              variant="link"
              onClick={() => setShowForgotPassword(true)}
              className="w-full text-red-600 hover:text-red-700 p-0 h-auto"
            >
              Forgot Password?
            </Button>

            <Button 
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="w-full h-11 flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminAuthPage;